import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, RequireVerifiedEmail, RequireAdmin, RequireFeatureFlag } from './components/ProtectedRoute';
import { GooFilter } from './design/primitives';
import { CookieConsent } from './components/CookieConsent/CookieConsent';
import { ScrollToTop } from './components/ScrollToTop';
// The marketing homepage is the LCP entry point, so it stays in the main bundle
// (no Suspense flash on first paint). Every other route is code-split into its
// own chunk so the landing page no longer ships the whole app's JS — this is
// what cuts the "reduce unused JavaScript" payload for first-time visitors.
import { MarketingPage } from './pages/MarketingPage';

// React.lazy expects a default export; these pages are named exports, so each
// factory re-maps the named export onto `default`.
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const MagicLinkLandingPage = lazy(() => import('./pages/auth/MagicLinkLandingPage').then((m) => ({ default: m.MagicLinkLandingPage })));
const InviteLandingPage = lazy(() => import('./pages/auth/InviteLandingPage').then((m) => ({ default: m.InviteLandingPage })));
const AccountSetupPage = lazy(() => import('./pages/auth/AccountSetupPage').then((m) => ({ default: m.AccountSetupPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const SchedulerPage = lazy(() => import('./pages/SchedulerPage').then((m) => ({ default: m.SchedulerPage })));
const ClearedPage = lazy(() => import('./pages/cleared/ClearedPage').then((m) => ({ default: m.ClearedPage })));
const HouseholdPage = lazy(() => import('./pages/household/HouseholdPage').then((m) => ({ default: m.HouseholdPage })));
const AccountSettingsPage = lazy(() => import('./pages/account/AccountSettingsPage').then((m) => ({ default: m.AccountSettingsPage })));
const ManageAccountsPage = lazy(() => import('./pages/accounts/ManageAccountsPage').then((m) => ({ default: m.ManageAccountsPage })));
const PlaidOAuthReturn = lazy(() => import('./pages/accounts/PlaidOAuthReturn').then((m) => ({ default: m.PlaidOAuthReturn })));
const HelpPage = lazy(() => import('./pages/help/HelpPage').then((m) => ({ default: m.HelpPage })));
const FeatureRequestsPage = lazy(() => import('./pages/featureRequests/FeatureRequestsPage').then((m) => ({ default: m.FeatureRequestsPage })));
const BillingPage = lazy(() => import('./pages/billing/BillingPage').then((m) => ({ default: m.BillingPage })));
const ScenariosPage = lazy(() => import('./pages/scenarios/ScenariosPage').then((m) => ({ default: m.ScenariosPage })));
const GoalsPage = lazy(() => import('./pages/goals/GoalsPage').then((m) => ({ default: m.GoalsPage })));
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage').then((m) => ({ default: m.InsightsPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminAuditLogPage = lazy(() => import('./pages/admin/AdminAuditLogPage').then((m) => ({ default: m.AdminAuditLogPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsOfUsePage = lazy(() => import('./pages/legal/TermsOfUsePage').then((m) => ({ default: m.TermsOfUsePage })));
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage').then((m) => ({ default: m.BlogIndexPage })));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage').then((m) => ({ default: m.BlogPostPage })));

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <GooFilter />
      <CookieConsent />
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<MarketingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account-setup" element={<AccountSetupPage />} />
        <Route path="/auth/magic" element={<MagicLinkLandingPage />} />
        <Route path="/invite/:token" element={<InviteLandingPage />} />
        {/* /verify-email accepts a `?token=` query from the verification email
            (token consumption needs no auth) and otherwise renders the gate
            for signed-in unverified users. The page itself handles the
            signed-in / signed-out branching. */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <SchedulerPage />
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cleared"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <ClearedPage />
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/household"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <HouseholdPage />
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <AccountSettingsPage />
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <HelpPage />
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feature-requests"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <FeatureRequestsPage />
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireFeatureFlag flag="gooey_plus">
                  <BillingPage />
                </RequireFeatureFlag>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireFeatureFlag flag="gooey_plus">
                  <ManageAccountsPage />
                </RequireFeatureFlag>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plaid/oauth-return"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireFeatureFlag flag="gooey_plus">
                  <PlaidOAuthReturn />
                </RequireFeatureFlag>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scenarios"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireFeatureFlag flag="gooey_plus">
                  <ScenariosPage />
                </RequireFeatureFlag>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/goals"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireFeatureFlag flag="gooey_plus">
                  <GoalsPage />
                </RequireFeatureFlag>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/insights"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireFeatureFlag flag="gooey_plus">
                  <InsightsPage />
                </RequireFeatureFlag>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireAdmin>
                  <AdminUsersPage />
                </RequireAdmin>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-log"
          element={
            <ProtectedRoute>
              <RequireVerifiedEmail>
                <RequireAdmin>
                  <AdminAuditLogPage />
                </RequireAdmin>
              </RequireVerifiedEmail>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
